import axios from 'axios';
import { refreshSession } from './authkit';
import { authkitSession, logout } from '../actions/user';

const REFRESH_MARGIN_MS = 60 * 1000;
const RETRY_DELAY_MS = 30 * 1000;

// Keeps the AuthKit session alive: proactively renews the access token before
// it expires, and retries a 401'd API request once after refreshing.
export default function initAuth(store) {
  let timer = null;
  let lastToken = null;

  const currentToken = () => store.getState().currentUser.token;

  const doRefresh = token => refreshSession(token).then(
    (fresh) => {
      store.dispatch(authkitSession(fresh));
      return fresh;
    },
    (error) => {
      // Only drop the session when AuthKit rejected the refresh token; a
      // network blip shouldn't log the user out.
      if (error.status && error.status < 500) {
        store.dispatch(logout());
      }
      throw error;
    },
  );

  const schedule = (token) => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (!token || !token.refresh_token || !token.expires) {
      return;
    }
    const attempt = () => doRefresh(token).catch(() => {
      if (currentToken() === token) {
        timer = setTimeout(attempt, RETRY_DELAY_MS);
      }
    });
    const delay = Math.max(token.expires - REFRESH_MARGIN_MS - new Date().getTime(), 0);
    timer = setTimeout(attempt, delay);
  };

  store.subscribe(() => {
    const token = currentToken();
    if (token !== lastToken) {
      lastToken = token;
      schedule(token);
    }
  });

  axios.interceptors.response.use(undefined, (error) => {
    const token = currentToken();
    const { config, response } = error;
    if (!response || response.status !== 401 || !config || config.authRetried
      || !token || !token.refresh_token) {
      return Promise.reject(error);
    }
    config.authRetried = true;
    // If a refresh already landed while this request was in flight, the 401
    // just means it was sent with the old token — retry with the current one
    // instead of burning another one-time-use refresh token.
    const sentAuth = config.headers && config.headers.Authorization;
    if (sentAuth && sentAuth !== `Bearer ${token.access_token}`) {
      config.headers = { ...config.headers, Authorization: `Bearer ${token.access_token}` };
      return axios(config);
    }
    return doRefresh(token).then(
      (fresh) => {
        config.headers = { ...config.headers, Authorization: `Bearer ${fresh.access_token}` };
        return axios(config);
      },
      () => Promise.reject(error),
    );
  });

  lastToken = currentToken();
  schedule(lastToken);
}
